import Dayjs from 'dayjs'
import { jsPDF } from 'jspdf'

// Build a proper Purchase Order PDF for an approved program-material order and
// bulk-download a set of them (one PDF per PO, zipped when there's more than one).
// jsPDF is already a dep; jszip is loaded lazily only when a ZIP is needed.

// jsPDF's built-in fonts don't carry the ₹ glyph, so use "Rs." in the PDF.
const inr = (n) =>
  'Rs. ' +
  Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })

const STATUS_LABEL = {
  approved: 'Approved',
  sent: 'Sent to centre',
  received: 'Received',
  fulfilled: 'Fulfilled',
  submitted: 'Submitted',
  rejected: 'Rejected'
}

// PO numbers contain '/', which is illegal in filenames — sanitise.
const safeName = (po) => String(po || 'PO').replace(/[\\/:*?"<>|]/g, '-')

const clip = (v, max) => {
  const s = String(v == null ? '' : v)
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Render one order as a jsPDF document (portrait A4).
const buildPoDoc = (order) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const m = 40
  let y = 52

  // Header band
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.text('OpenHouse', m, y)
  doc.setFontSize(15)
  doc.text('PURCHASE ORDER', W - m, y, { align: 'right' })
  y += 12
  doc.setDrawColor(170)
  doc.line(m, y, W - m, y)
  y += 24

  // Meta block
  doc.setFontSize(10)
  const meta = (label, value, x) => {
    doc.setFont('helvetica', 'bold')
    doc.text(label, x, y)
    doc.setFont('helvetica', 'normal')
    doc.text(String(value == null || value === '' ? '—' : value), x + 64, y)
  }
  meta('PO Number:', order.po_number, m)
  meta('Date:', Dayjs(order.month).format('MMMM YYYY'), W / 2)
  y += 16
  meta('Centre:', order.centreName, m)
  meta('Status:', STATUS_LABEL[order.status] || order.status, W / 2)
  y += 16
  // Make clear which procurement category this PO is for.
  meta('Type:', order.poType || 'Program Material', m)
  y += 24

  // Line-item table
  const cols = [
    { k: 'idx', label: '#', x: m, w: 22, align: 'left' },
    { k: 'item_name', label: 'Item', x: m + 22, w: 232, align: 'left' },
    { k: 'reference_id', label: 'Ref ID', x: m + 254, w: 92, align: 'left' },
    { k: 'qty', label: 'Qty', x: m + 346, w: 42, align: 'right' },
    { k: 'unit_cost', label: 'Unit Cost', x: m + 388, w: 62, align: 'right' },
    { k: 'line_total', label: 'Line Total', x: m + 450, w: 65, align: 'right' }
  ]
  const draw = (cells, bold) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    cols.forEach((c) => {
      const t = String(cells[c.k] == null ? '' : cells[c.k])
      doc.text(t, c.align === 'right' ? c.x + c.w : c.x + 2, y, {
        align: c.align
      })
    })
  }

  doc.setFillColor(238)
  doc.rect(m, y - 11, W - 2 * m, 17, 'F')
  draw(
    {
      idx: '#',
      item_name: 'Item',
      reference_id: 'Ref ID',
      qty: 'Qty',
      unit_cost: 'Unit Cost',
      line_total: 'Line Total'
    },
    true
  )
  y += 18

  const lines = order.lines || []
  let computed = 0
  lines.forEach((l, i) => {
    if (y > H - 130) {
      doc.addPage()
      y = 52
    }
    computed += Number(l.line_total || 0)
    draw(
      {
        idx: i + 1,
        item_name: clip(l.item_name, 54),
        reference_id: l.reference_id || '—',
        qty: Number(l.qty || 0),
        unit_cost: inr(l.unit_cost),
        line_total: inr(l.line_total)
      },
      false
    )
    y += 16
  })
  if (!lines.length) {
    doc.setFont('helvetica', 'normal')
    doc.text('No items.', m + 2, y)
    y += 16
  }

  // Grand total
  doc.setDrawColor(200)
  doc.line(m, y - 4, W - m, y - 4)
  y += 14
  doc.setFont('helvetica', 'bold')
  doc.text('Grand Total', m + 346, y)
  doc.text(inr(order.total != null ? order.total : computed), m + 515, y, {
    align: 'right'
  })
  doc.setFont('helvetica', 'normal')
  y += 26

  // HQ remarks
  if (order.review_remarks) {
    doc.setFont('helvetica', 'bold')
    doc.text('HQ remarks:', m, y)
    doc.setFont('helvetica', 'normal')
    const wrapped = doc.splitTextToSize(
      String(order.review_remarks),
      W - 2 * m - 66
    )
    doc.text(wrapped, m + 66, y)
  }

  // Approver + signature, anchored near the bottom of the last page
  const sigY = H - 64
  doc.setFont('helvetica', 'bold')
  doc.text('Approved by HQ', m, sigY)
  doc.setFont('helvetica', 'normal')
  doc.setDrawColor(120)
  doc.line(W - m - 170, sigY, W - m, sigY)
  doc.setFontSize(8)
  doc.text('Authorised signature', W - m - 170, sigY + 12)
  doc.setTextColor(150)
  doc.text(
    `Generated from OpenHouse Procurement · ${Dayjs().format('D MMM YYYY')}`,
    m,
    H - 30
  )

  return doc
}

// Download POs for the given orders: a single PDF if one, else a ZIP of PDFs
// each named after its PO number. Returns the number downloaded.
export const downloadProgramMaterialPos = async (orders) => {
  const eligible = (orders || []).filter((o) => o && o.po_number)
  if (!eligible.length) return 0

  if (eligible.length === 1) {
    buildPoDoc(eligible[0]).save(`${safeName(eligible[0].po_number)}.pdf`)
    return 1
  }

  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  const used = {}
  eligible.forEach((o) => {
    let name = `${safeName(o.po_number)}.pdf`
    if (used[name]) name = `${safeName(o.po_number)}-${used[name] + 1}.pdf`
    used[`${safeName(o.po_number)}.pdf`] = (used[`${safeName(o.po_number)}.pdf`] || 0) + 1
    zip.file(name, buildPoDoc(o).output('blob'))
  })
  const content = await zip.generateAsync({ type: 'blob' })
  const stamp = Dayjs().format('YYYY-MM-DD')
  triggerDownload(content, `purchase-orders-${stamp}.zip`)
  return eligible.length
}
